import React, { useState, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  Skeleton,
  Chip,
  Divider,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { grantSupportRole, getSupportAuditFeed } from 'apis/supportAPIs';
import { SupportRoleGuard } from './components';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    flexGrow: 1,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  form: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

const SupportAdmin = () => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const [identifier, setIdentifier] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const grantMutation = useMutation({
    mutationFn: grantSupportRole,
    onSuccess: (data) => {
      setSuccessMsg(data?.message || 'Role updated successfully.');
      setErrorMsg('');
      setIdentifier('');
      queryClient.invalidateQueries({ queryKey: ['supportUsers'] });
    },
    onError: (err) => {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to update role');
      setSuccessMsg('');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: grantSupportRole,
    onSuccess: (data) => {
      setSuccessMsg(data?.message || 'Role revoked successfully.');
      setErrorMsg('');
      queryClient.invalidateQueries({ queryKey: ['supportUsers'] });
    },
    onError: (err) => {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to revoke role');
      setSuccessMsg('');
    },
  });

  const handleGrant = () => {
    if (!identifier.trim()) return;
    setSuccessMsg('');
    setErrorMsg('');
    const isEmail = identifier.includes('@');
    grantMutation.mutate({
      [isEmail ? 'email' : 'uid']: identifier.trim(),
      action: 'grant',
    });
  };

  const handleRevoke = (uid, email) => {
    setSuccessMsg('');
    setErrorMsg('');
    revokeMutation.mutate({
      uid: uid || undefined,
      email: email || undefined,
      action: 'revoke',
    });
  };

  const {
    data: auditData,
    isLoading: usersLoading,
  } = useQuery({
    queryKey: ['supportUsers'],
    queryFn: () => getSupportAuditFeed({ type: 'grantRole' }),
    staleTime: 30000,
  });

  const supportUsers = useMemo(() => {
    if (!auditData) return [];
    const events = Array.isArray(auditData) ? auditData : auditData?.events || [];
    const userMap = new Map();
    events.forEach((e) => {
      if (e.detail) {
        try {
          const d = typeof e.detail === 'string' ? JSON.parse(e.detail) : e.detail;
          if (d.email) {
            userMap.set(d.email, {
              email: d.email,
              uid: d.uid,
              grantedBy: e.actor,
              grantedAt: e.timestamp,
            });
          }
        } catch {
          // ignore parse errors
        }
      }
    });
    return Array.from(userMap.values());
  }, [auditData]);

  return (
    <SupportRoleGuard requiredRole="supportAdmin">
      <div className={classes.root}>
        <Typography variant="h4" gutterBottom>
          Support Admin
        </Typography>

        <div className={classes.section}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Grant Support Role
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Enter a user&apos;s email address or Firebase UID to grant the support role.
              </Typography>

              {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
              {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

              <div className={classes.form}>
                <TextField
                  label="Email or UID"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  size="small"
                  sx={{ minWidth: 300 }}
                  inputProps={{ 'aria-label': 'User email or UID' }}
                />
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleGrant}
                  disabled={!identifier.trim() || grantMutation.isPending}
                >
                  {grantMutation.isPending ? 'Granting…' : 'Grant Role'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <Divider sx={{ mb: 3 }} />

        <div className={classes.section}>
          <Typography variant="h6" gutterBottom>
            Current Support Users
          </Typography>

          {usersLoading ? (
            <Box>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} height={48} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : supportUsers.length === 0 ? (
            <Card>
              <CardContent className={classes.emptyState}>
                <Typography variant="body1" color="textSecondary">
                  No support users found. Grant a role above to get started.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <TableContainer component={Paper}>
              <Table aria-label="Support users">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>UID</TableCell>
                    <TableCell>Granted By</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supportUsers.map((u) => (
                    <TableRow key={u.email || u.uid}>
                      <TableCell>{u.email || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {u.uid || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{u.grantedBy || '—'}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<PersonRemoveIcon />}
                          onClick={() => handleRevoke(u.uid, u.email)}
                          disabled={revokeMutation.isPending}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>
    </SupportRoleGuard>
  );
};

export default SupportAdmin;
